import React, { useState, useEffect, useMemo } from "react";
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    Search,
    Loader2,
    AlertCircle,
    CreditCard,
    RefreshCw,
    AlertTriangle,
    Mail,
    Link,
    ExternalLink,
    UserPlus,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { useAuth } from "@/contexts/AuthContext";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { apiCall } from "@/utils/api";
import { toast } from "sonner";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog";

interface Student {
    id: number;
    email: string;
    first_name: string | null;
    last_name: string | null;
    stripe_customer_id: string | null;
    stripe_subscription_id: string | null;
    stripe_product_id: string | null;
    stripe_product_name: string | null;
    learnybox_formation_id: string | null;
    learnybox_membre_id: string | null;
    formation_status: string;
    payment_status: string;
    trial_started_at: number | null;
    trial_end_at: number | null;
    purchased_at: number | null;
    last_payment_at: number | null;
    next_payment_at: number | null;
    activated_at: number | null;
    learnybox_synced_at: number | null;
    created_at: number | null;
    needs_attention: boolean;
    mismatch_type: string | null;
    mismatch_message: string | null;
}

const InscritsFormation: React.FC = () => {
    const [searchTerm, setSearchTerm] = useState<string>("");
    const [filterStatus, setFilterStatus] = useState<string>("all");
    const [filterFormation, setFilterFormation] = useState<string>("all");
    const [filterDateInscription, setFilterDateInscription] = useState<string>("");
    const [students, setStudents] = useState<Student[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [actionLoading, setActionLoading] = useState<number | null>(null);
    const [syncing, setSyncing] = useState<boolean>(false);
    const [addingToLearnybox, setAddingToLearnybox] = useState<number | null>(null);

    // Confirmation Dialog State
    const [confirmDialog, setConfirmDialog] = useState<{
        show: boolean;
        studentId: number | null;
        action: "activate" | "disable" | null;
        studentName: string | null;
    }>({ show: false, studentId: null, action: null, studentName: null });

    const { token } = useAuth();

    const fetchStudents = async () => {
        try {
            setLoading(true);
            const result = await apiCall<Student[] | { error: string }>(
                token,
                "/stripe/subscriptions"
            );

            if (Array.isArray(result)) {
                setStudents(result);
            } else {
                toast.error("Erreur de chargement", {
                    description: "Impossible de charger les étudiants."
                });
                setStudents([]);
            }
        } catch (err) {
            console.error("Error fetching students:", err);
            toast.error("Erreur de chargement", {
                description: "Une erreur est survenue lors du chargement."
            });
            setStudents([]);
        } finally {
            setLoading(false);
        }
    };

    const handleRefresh = async () => {
        try {
            setSyncing(true);

            // First, sync from Stripe
            const syncResult = await apiCall<{ message: string; success: boolean; stats?: any; error?: string }>(
                token,
                "/stripe/sync-from-stripe",
                "POST"
            );

            if (syncResult.error || !syncResult.success) {
                toast.error("Erreur de synchronisation", {
                    description: syncResult.error || "La synchronisation a échoué."
                });
                setSyncing(false);
                return;
            }

            // Show success message with stats if available
            if (syncResult.stats) {
                const { processed, created, updated } = syncResult.stats;
                toast.success("Synchronisation réussie", {
                    description: `${processed} abonnements traités (${created} créés, ${updated} mis à jour)`
                });
            } else {
                toast.success("Synchronisation réussie");
            }

            // Then, fetch the updated list
            await fetchStudents();
        } catch (err) {
            console.error("Error syncing from Stripe:", err);
            toast.error("Erreur de synchronisation", {
                description: "Une erreur est survenue lors de la synchronisation."
            });
        } finally {
            setSyncing(false);
        }
    };

    useEffect(() => {
        if (token) {
            fetchStudents();
        }
    }, [token]);

    const formations = useMemo(() => {
        const unique = new Map<string, string>(); // Map of product_id -> product_name
        students.forEach(s => {
            if (s.stripe_product_id) {
                unique.set(s.stripe_product_id, s.stripe_product_name || s.stripe_product_id);
            }
            if (s.learnybox_formation_id) {
                unique.set(s.learnybox_formation_id, String(s.learnybox_formation_id));
            }
        });
        return Array.from(unique.entries()).sort((a, b) => {
            const nameA = String(a[1] || '');
            const nameB = String(b[1] || '');
            return nameA.localeCompare(nameB);
        });
    }, [students]);

    const filteredStudents = useMemo(() => {
        return students.filter((student) => {
            const fullName = `${student.first_name || ''} ${student.last_name || ''}`.trim();
            const matchesSearch =
                fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                student.email.toLowerCase().includes(searchTerm.toLowerCase());

            const matchesStatus =
                filterStatus === "all" ||
                student.formation_status === filterStatus;

            const matchesFormation =
                filterFormation === "all" ||
                student.stripe_product_id === filterFormation ||
                student.learnybox_formation_id === filterFormation;

            const matchesDateInscription = !filterDateInscription ||
                (student.created_at && new Date(student.created_at * 1000).toISOString().startsWith(filterDateInscription));

            return matchesSearch && matchesStatus && matchesFormation && matchesDateInscription;
        });
    }, [students, searchTerm, filterStatus, filterFormation, filterDateInscription]);

    const handleAction = async () => {
        const { studentId, action } = confirmDialog;
        if (!studentId || !action) return;

        try {
            setActionLoading(studentId);
            setConfirmDialog({ ...confirmDialog, show: false });

            const result = await apiCall<{ message: string; student: Student; error?: string }>(
                token,
                `/stripe/subscriptions/${studentId}/toggle`,
                "POST",
                { action }
            );

            if (result.error) {
                toast.error("Erreur", { description: result.error });
            } else {
                toast.success("Succès", { description: "Statut mis à jour avec succès." });
                // Update local state
                setStudents((prev) =>
                    prev.map((s) => (s.id === studentId ? { ...s, ...result.student } : s))
                );
            }
        } catch (err) {
            console.error("Error updating student:", err);
            toast.error("Erreur", { description: "Une erreur est survenue." });
        } finally {
            setActionLoading(null);
            setConfirmDialog({ show: false, studentId: null, action: null, studentName: null });
        }
    };

    const openConfirmDialog = (student: Student, action: "activate" | "disable") => {
        setConfirmDialog({
            show: true,
            studentId: student.id,
            action: action,
            studentName: student.email,
        });
    };

    const handleAddToLearnybox = async (student: Student) => {
        try {
            setAddingToLearnybox(student.id);
            const result = await apiCall<{ message: string; student?: Partial<Student>; error?: string }>(
                token,
                `/stripe/subscriptions/${student.id}/add-to-learnybox`,
                "POST"
            );

            if (result.error) {
                toast.error("Erreur", { description: result.error });
            } else {
                toast.success("Succès", { description: `${student.email} ajouté à Learnybox.` });
                setStudents((prev) =>
                    prev.map((s) => (s.id === student.id ? { ...s, ...result.student } : s))
                );
            }
        } catch (err) {
            console.error("Error adding to Learnybox:", err);
            toast.error("Erreur", { description: "Impossible d'ajouter l'étudiant à Learnybox." });
        } finally {
            setAddingToLearnybox(null);
        }
    };

    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'active':
                return <Badge className="bg-emerald-500">Actif</Badge>;
            case 'inactive':
                return <Badge variant="destructive">Inactif</Badge>;
            case 'pending':
                return <Badge variant="outline">En attente</Badge>;
            default:
                return <Badge variant="outline">{status}</Badge>;
        }
    };

    const getPaymentStatusBadge = (student: Student) => {
        const status = student.payment_status;
        switch (status) {
            case 'active': {
                const nextPayment = student.next_payment_at
                    ? new Date(student.next_payment_at * 1000).toLocaleDateString()
                    : null;
                return (
                    <Badge variant="outline" className="text-emerald-600 border-emerald-200 bg-emerald-50">
                        Paiement OK
                        {nextPayment && (
                            <span className="ml-1 text-[10px] font-normal text-emerald-700">
                                (prochain: {nextPayment})
                            </span>
                        )}
                    </Badge>
                );
            }
            case 'incomplete':
            case 'incomplete_expired':
            case 'past_due':
            case 'unpaid':
                return <Badge variant="destructive">Paiement Échoué</Badge>;
            case 'canceled':
                return <Badge variant="secondary">Annulé</Badge>;
            case 'trialing': {
                const end = student.trial_end_at
                    ? new Date(student.trial_end_at * 1000).toLocaleDateString()
                    : null;
                return (
                    <Badge variant="secondary" className="bg-blue-100 text-blue-700 hover:bg-blue-100">
                        Essai
                        {end && (
                            <span className="ml-1 text-[10px] font-normal text-blue-800">
                                (jusqu'au {end})
                            </span>
                        )}
                    </Badge>
                );
            }
            default:
                return <Badge variant="outline" className="text-gray-500">{status}</Badge>;
        }
    };

    return (
        <div className="container mx-auto py-8 px-4 space-y-8 animate-in fade-in duration-700">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                <div className="space-y-1">
                    <div className="flex items-center gap-3">
                        <div className="p-2.5 bg-primary/10 rounded-2xl">
                            <CreditCard className="h-6 w-6 text-primary" />
                        </div>
                        <h1 className="text-3xl font-black tracking-tight text-slate-900 uppercase">Inscrits Formation</h1>
                    </div>
                    <p className="text-slate-500 font-medium ml-14">Gérez les accès aux formations et suivez les statuts de paiement synchronisés depuis Stripe.</p>
                </div>
                <div className="flex items-center gap-3 ml-14 md:ml-0">
                    <Button
                        variant="outline"
                        onClick={handleRefresh}
                        disabled={syncing || loading}
                        className="rounded-2xl h-12 px-6 font-bold bg-white shadow-xl shadow-slate-200/50 border-slate-100 hover:bg-slate-50 transition-all hover:scale-105 active:scale-95"
                    >
                        <RefreshCw className={cn("h-4 w-4 mr-2", (syncing || loading) ? "animate-spin" : "")} />
                        {syncing ? "Synchronisation..." : "Actualiser Stripe"}
                    </Button>
                </div>
            </div>

            {/* Filters & Content */}
            <Card className="border-none shadow-2xl shadow-slate-200/40 rounded-[2.5rem] overflow-hidden bg-white/80 backdrop-blur-xl">
                <CardHeader className="bg-white/50 border-b border-slate-100/50 p-8">
                    <div className="flex flex-col gap-6">
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                            <div className="flex gap-2 p-1.5 bg-slate-100/80 rounded-2xl w-fit">
                                {[
                                    { id: 'all', label: 'Tous' },
                                    { id: 'active', label: 'Actifs' },
                                    { id: 'inactive', label: 'Inactifs' }
                                ].map((tab) => (
                                    <button
                                        key={tab.id}
                                        onClick={() => setFilterStatus(tab.id)}
                                        className={cn(
                                            "px-6 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all",
                                            filterStatus === tab.id
                                                ? "bg-white text-primary shadow-sm ring-1 ring-slate-200"
                                                : "text-slate-500 hover:text-slate-700"
                                        )}
                                    >
                                        {tab.label}
                                    </button>
                                ))}
                            </div>

                            <div className="relative w-full md:w-96 group">
                                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 group-focus-within:text-primary transition-colors" />
                                <Input
                                    placeholder="Rechercher par email, nom..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="pl-12 h-12 bg-white border-slate-200 focus:ring-primary/20 transition-all rounded-2xl text-sm font-medium shadow-sm"
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div className="space-y-2">
                                <label className="text-[10px] uppercase font-black text-slate-400 tracking-widest ml-1">Formation</label>
                                <SearchableSelect
                                    value={filterFormation}
                                    onValueChange={setFilterFormation}
                                    placeholder="Toutes les formations"
                                    className="w-full h-12 bg-white border-slate-100 rounded-2xl font-bold shadow-sm"
                                    options={[
                                        { value: "all", label: "Toutes les formations" },
                                        ...formations.map(([id, name]) => ({ value: id, label: name })),
                                    ]}
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-[10px] uppercase font-black text-slate-400 tracking-widest ml-1">Date d'inscription</label>
                                <Input
                                    type="date"
                                    value={filterDateInscription}
                                    onChange={(e) => setFilterDateInscription(e.target.value)}
                                    className="h-12 bg-white border-slate-100 rounded-2xl font-bold shadow-sm"
                                />
                            </div>
                            <div className="flex items-end">
                                <Button
                                    variant="ghost"
                                    onClick={() => {
                                        setFilterStatus("all");
                                        setFilterFormation("all");
                                        setFilterDateInscription("");
                                        setSearchTerm("");
                                    }}
                                    className="h-12 rounded-2xl text-slate-400 hover:text-red-500 font-black uppercase tracking-widest text-[10px] w-full border border-dashed border-slate-200 hover:border-red-100"
                                >
                                    Réinitialiser les filtres
                                </Button>
                            </div>
                        </div>
                    </div>
                </CardHeader>

                <CardContent className="p-0">
                    <div className="overflow-x-auto">
                        {loading ? (
                            <div className="p-12 text-center">
                                <Loader2 className="h-10 w-10 animate-spin text-primary mx-auto mb-4" />
                                <p className="text-slate-500 font-bold">Chargement des étudiants...</p>
                            </div>
                        ) : (
                            <Table>
                                <TableHeader>
                                    <TableRow className="border-slate-50 hover:bg-transparent">
                                        <TableHead className="pl-8 text-[11px] font-black uppercase tracking-widest text-slate-400">Identité / Email</TableHead>
                                        <TableHead className="text-[11px] font-black uppercase tracking-widest text-slate-400">Formation (Stripe)</TableHead>
                                        <TableHead className="text-[11px] font-black uppercase tracking-widest text-slate-400">Inscription</TableHead>
                                        <TableHead className="text-[11px] font-black uppercase tracking-widest text-slate-400">Paiement / Cycle</TableHead>
                                        <TableHead className="text-[11px] font-black uppercase tracking-widest text-slate-400">Accès LMS</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {filteredStudents.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={9} className="h-32 text-center text-slate-400 font-medium">
                                                Aucun étudiant trouvé.
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        filteredStudents.map((student) => (
                                            <TableRow key={student.id} className="group border-slate-50 hover:bg-slate-50/50 transition-colors">
                                                <TableCell className="pl-8 py-6">
                                                    <div className="flex flex-col gap-1">
                                                        <div className="flex items-center gap-2">
                                                            {student.needs_attention && (
                                                                <div title={student.mismatch_message || "Incohérence"}>
                                                                    <AlertTriangle className="h-4 w-4 text-orange-500 animate-pulse" />
                                                                </div>
                                                            )}
                                                            <span className="font-semibold text-slate-900 text-sm tracking-tight">
                                                                {student.first_name || student.last_name ? `${student.first_name || ''} ${student.last_name || ''}`.trim() : student.email.split('@')[0]}
                                                            </span>
                                                        </div>
                                                        <div className="text-xs font-bold text-slate-400 flex items-center gap-1.5 mt-0.5">
                                                            <Mail className="h-3 w-3" />
                                                            {student.email}
                                                        </div>
                                                    </div>
                                                </TableCell>
                                                <TableCell className="text-slate-500">
                                                    <div className="flex flex-col">
                                                        <span className="text-sm font-semibold text-slate-700 capitalize">{student.stripe_product_name || "Produit Inconnu"}</span>
                                                        <span className="text-xs font-bold text-slate-400">{student.stripe_product_id || "N/A"}</span>
                                                    </div>
                                                </TableCell>
                                                <TableCell className="text-slate-500">
                                                    <span className="text-xs font-black text-slate-400 uppercase tracking-widest">
                                                        {student.purchased_at ? new Date(student.purchased_at * 1000).toLocaleDateString() : 'N/A'}
                                                    </span>
                                                </TableCell>
                                                <TableCell>
                                                    <div className="flex flex-col gap-1.5">
                                                        {getPaymentStatusBadge(student)}
                                                        {student.last_payment_at && (
                                                            <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest ml-1">
                                                                Dernier: {new Date(student.last_payment_at * 1000).toLocaleDateString()}
                                                            </span>
                                                        )}
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    <div className="flex items-center gap-2">
                                                        <Badge className={cn(
                                                            "text-[10px] font-semibold capitalize px-3 py-1 rounded-xl border-none shadow-sm",
                                                            student.formation_status === 'active' ? "bg-emerald-50 text-emerald-600 shadow-emerald-200/50" : "bg-slate-100 text-slate-400"
                                                        )}>
                                                            {student.formation_status === 'active' ? "✓ Ouvert" : "× Fermé"}
                                                        </Badge>
                                                        {student.learnybox_membre_id ? (
                                                            <Button
                                                                variant="ghost"
                                                                size="icon"
                                                                className="h-8 w-8 rounded-xl text-slate-400 hover:text-primary hover:bg-primary/5 transition-all"
                                                                onClick={() => window.open(`https://amc.learnybox.com/app/user/${student.learnybox_membre_id}/?tab=formation`, '_blank')}
                                                            >
                                                                <ExternalLink className="h-3.5 w-3.5" />
                                                            </Button>
                                                        ) : (
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                className="h-8 rounded-xl text-xs font-semibold text-blue-600 hover:text-blue-700 hover:bg-blue-50 transition-all"
                                                                onClick={() => handleAddToLearnybox(student)}
                                                                disabled={addingToLearnybox === student.id}
                                                            >
                                                                {addingToLearnybox === student.id ? (
                                                                    <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
                                                                ) : (
                                                                    <UserPlus className="h-3.5 w-3.5 mr-1" />
                                                                )}
                                                                Ajouter
                                                            </Button>
                                                        )}
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        )}
                    </div>
                </CardContent>
            </Card>

            {/* Confirmation Dialog */}
            <Dialog open={confirmDialog.show} onOpenChange={(open) => !open && setConfirmDialog(d => ({ ...d, show: false }))}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{confirmDialog.action === 'disable' ? 'Désactiver l\'accès ?' : 'Activer l\'accès ?'}</DialogTitle>
                        <DialogDescription>
                            {confirmDialog.action === 'disable'
                                ? `Êtes-vous sûr de vouloir désactiver l'accès pour ${confirmDialog.studentName} ?`
                                : `Êtes-vous sûr de vouloir activer l'accès pour ${confirmDialog.studentName} ?`
                            }
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setConfirmDialog(d => ({ ...d, show: false }))}>Annuler</Button>
                        <Button
                            variant={confirmDialog.action === 'disable' ? "destructive" : "default"}
                            onClick={handleAction}
                        >
                            Confirmer
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default InscritsFormation;
